// Donor-portal profile API helper — CHG-1513 portal-pivot retarget.
//
// Backend: portal.py canonical at GET + PATCH /api/v1/my-giving/me
// (AccountRead schema). Replaces the CHG-1478 (α.1) shim that called
// /api/v1/me/profile.
//
// Wave 1 schema differences vs CHG-1478 shim:
//   - +profile_complete, +has_password (new flags from portal AccountRead)
//   - +status, +last_login_at, +created_at (informational)
//   - -preferred_communication_channel, -intent_topics (shim-only;
//     portal.py /preferences handles communication channel via the
//     separate DonorCommunicationPreferences shape on /preferences)
// MyProfile.tsx adapts to the new fields; communication channel fields
// land on the Preferences page instead.

import { apiClient } from "./client";

export interface MeProfile {
  id: string;
  email: string;
  email_verified: boolean;
  full_name: string;
  /**
   * @deprecated CHG-2939 — read-only compatibility alias: the backend
   * serializes the canonical `phone_e164` here when a verified phone exists,
   * else the legacy raw value. New code reads `phone_e164` +
   * `phone_verified_at`. NOT writable — see MeProfilePatch.
   */
  phone_number: string | null;
  /** Canonical E.164 phone (Account.phone_e164), written only by the OTP confirm flow (CHG-2939). */
  phone_e164?: string | null;
  phone_verified_at?: string | null;
  status: string;
  last_login_at: string | null;
  created_at: string;
  preferred_timezone: string | null;
  preferred_locale: string | null;
  preferred_language: string | null;
  profile_complete: boolean;
  has_password: boolean;
}

export interface MeProfilePatch {
  full_name?: string;
  // CHG-2942 (Slice C, DP-R1) — phone_number REMOVED. Runtime AccountUpdate is
  // extra="forbid": sending phone_number to PATCH /my-giving/me returns 422.
  // Phone changes go ONLY through the OTP pair below
  // (requestPhoneChange/confirmPhoneChange). Do not reintroduce.
  preferred_timezone?: string | null;
  preferred_locale?: string | null;
  preferred_language?: string | null;
}

export async function fetchMeProfile(): Promise<MeProfile> {
  const r = await apiClient().get<MeProfile>("/my-giving/me");
  return r.data;
}

export async function patchMeProfile(patch: MeProfilePatch): Promise<MeProfile> {
  const r = await apiClient().patch<MeProfile>("/my-giving/me", patch);
  return r.data;
}

// ---------------------------------------------------------------------------
// OTP-gated contact change (CHG-2942 / Slice C — UI for the CHG-2891 routes).
// Contract: specs/tier-04-interface/donor-portal.api.yaml
// (requestMyPhoneChange / confirmMyPhoneChange).
// ---------------------------------------------------------------------------

export interface ContactChangeRequestResult {
  /** Seconds until the staged change + OTP expire (backend: 600). */
  expires_in: number;
}

/** Stage a new phone + send a 6-digit SMS OTP to it (rate-limited 3/day). */
export async function requestPhoneChange(
  phone: string,
): Promise<ContactChangeRequestResult> {
  const r = await apiClient().post<ContactChangeRequestResult>(
    "/my-giving/me/request-phone-change",
    { phone },
  );
  return r.data;
}

/** Confirm the staged phone change with the OTP code; returns the updated profile. */
export async function confirmPhoneChange(code: string): Promise<MeProfile> {
  const r = await apiClient().post<MeProfile>(
    "/my-giving/me/confirm-phone-change",
    { code },
  );
  return r.data;
}

// C2 follow-up (CHG-2942 entry note): the email variant of the pair —
// POST /my-giving/me/request-email-change + /confirm-email-change — LANDED
// backend-side in CHG-2943 (Slice D, CHG-2933 §8 decision 1) during this
// CHG's close rebase. Helpers are intentionally still NOT defined here (C2
// is its own follow-up per PM scope): mirror
// requestPhoneChange/confirmPhoneChange and add a `kind="email"` entry to
// ContactChangeDialog's KIND_CONFIG.
